/**
 * Debt Management System
 * Handles all debt-related operations and calculations
 */

import { storage } from './storage';
import { DebtEngine } from '../utils/DebtEngine';

// Default data structure
const DEFAULT_DATA = {
  userId: null,
  profile: {
    name: null,
    email: null,
    createdAt: null,
    lastActive: null
  },
  debts: [],
  paymentHistory: [],
  projections: null,
  settings: {
    extraPayment: 0,
    currency: 'GBP',
    notificationsEnabled: true,
    reminderDay: 1
  },
  analytics: {
    totalDebtPaid: 0,
    totalInterestSaved: 0,
    debtsFreeDate: null,
    monthsAhead: 0
  }
};

export class DebtsManager {
  constructor() {
    this.data = this.loadData();
    this.listeners = new Set();
  }

  /**
   * Load data from storage
   */
  loadData() {
    const stored = storage.load();
    if (stored) {
      const data = { ...DEFAULT_DATA, ...stored };
      
      // Migrate existing debts if they're missing required fields
      const migratedData = this.migrateDebtFields(data);
      if (migratedData !== data) {
        this.saveData(migratedData);
        return migratedData;
      }
      
      return data;
    }
    
    // Check for legacy data and migrate
    const legacyData = this.migrateLegacyData();
    if (legacyData) {
      this.saveData(legacyData);
      return legacyData;
    }
    
    return { ...DEFAULT_DATA };
  }

  /**
   * Migrate existing debt data to include required fields
   */
  migrateDebtFields(data) {
    if (!data.debts || data.debts.length === 0) {
      return data;
    }

    let needsMigration = false;
    const migratedDebts = data.debts.map((debt, index) => {
      const migratedDebt = { ...debt };
      
      // Ensure minPayment field exists
      if (!migratedDebt.minPayment && migratedDebt.minPayment !== 0) {
        migratedDebt.minPayment = migratedDebt.regularPayment || 
          Math.max(25, Math.floor((migratedDebt.balance || migratedDebt.amount || 0) * 0.02));
        needsMigration = true;
        console.log(`[Migration] Added minPayment ${migratedDebt.minPayment} to debt: ${migratedDebt.name}`);
      }
      
      // Ensure order field exists and isn't the default fallback value
      if (!migratedDebt.order || migratedDebt.order === 999) {
        migratedDebt.order = index + 1; // Assign based on current array position
        needsMigration = true;
        console.log(`[Migration] ${migratedDebt.order === 999 ? 'Fixed default' : 'Added'} order ${index + 1} to debt: ${migratedDebt.name}`);
      }
      
      // Ensure balance field is consistent
      if (!migratedDebt.balance && migratedDebt.amount) {
        migratedDebt.balance = migratedDebt.amount;
        needsMigration = true;
        console.log(`[Migration] Set balance field for debt: ${migratedDebt.name}`);
      }
      
      // Ensure interestRate field exists
      if (!migratedDebt.interestRate && migratedDebt.interest) {
        migratedDebt.interestRate = migratedDebt.interest;
        needsMigration = true;
        console.log(`[Migration] Set interestRate field for debt: ${migratedDebt.name}`);
      }
      
      return migratedDebt;
    });

    if (needsMigration) {
      console.log('[DebtsManager] Successfully migrated existing debt fields');
      return {
        ...data,
        debts: migratedDebts,
        migratedAt: new Date().toISOString()
      };
    }

    return data;
  }

  /**
   * Migrate data from old localStorage keys
   */
  migrateLegacyData() {
    try {
      const oldDebts = localStorage.getItem('debtBalances');
      if (oldDebts) {
        console.log('[DebtsManager] Migrating legacy debt data');
        const debts = JSON.parse(oldDebts);
        return { ...DEFAULT_DATA, debts };
      }
    } catch (error) {
      console.warn('[DebtsManager] Error migrating legacy data:', error);
    }
    return null;
  }

  /**
   * Save data to storage
   */
  saveData(data = this.data) {
    this.data = {
      ...data,
      profile: {
        ...data.profile,
        lastActive: new Date().toISOString()
      }
    };
    
    storage.save(this.data);
    this.notifyListeners();
    
    // Legacy compatibility - also save to old key
    if (this.data.debts && this.data.debts.length > 0) {
      try {
        localStorage.setItem('debtBalances', JSON.stringify(this.data.debts));
      } catch (error) {
        console.warn('[DebtsManager] Error saving legacy compatibility data:', error);
      }
    }
  }

  /**
   * Set storage key for user-specific data
   */
  setUserStorageKey(userId) {
    if (userId) {
      storage.setKey(`trysnowball-user-data-${userId}`);
    } else {
      storage.setKey('trysnowball-user-data');
    }
  }

  /**
   * Reload data from storage
   */
  reloadData() {
    this.data = this.loadData();
    this.notifyListeners();
  }

  /**
   * Subscribe to data changes
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of data changes
   */
  notifyListeners() {
    this.listeners.forEach(callback => callback(this.data));
  }

  // DEBT OPERATIONS

  /**
   * CENTRALIZED DEBT ACCESSOR - The Only Official Way to Read Debt Data
   * All components, hooks, and pages should use this method exclusively.
   * This prevents data source mismatches and ensures consistent demo/real data handling.
   * 
   * @param {Object} options - Configuration options
   * @param {boolean} options.includeDemo - Whether to load demo data if no debts exist (default: true)
   * @param {boolean} options.sorted - Whether to return debts in user-defined order (default: true)
   * @returns {Array} Array of debt objects with consistent field structure
   */
  getDebts({ includeDemo = true, sorted = true } = {}) {
    // Return existing debts if available
    if (this.data?.debts && this.data.debts.length > 0) {
      const debts = this.data.debts;
      
      // Normalize field names for consistency (balance takes precedence over amount)
      const normalizedDebts = debts.map(debt => ({
        ...debt,
        // Ensure we always have a balance field
        balance: debt.balance ?? debt.amount ?? 0,
        // Preserve amount for backward compatibility
        amount: debt.balance ?? debt.amount ?? 0,
        // Ensure we have consistent interest field
        interest: debt.interest ?? debt.interestRate ?? 0,
        // Ensure minPayment field exists
        minPayment: debt.minPayment ?? debt.regularPayment ?? 0
      }));
      
      return sorted ? this._getSortedDebts(normalizedDebts) : normalizedDebts;
    }
    
    // Load demo data if no debts exist and demo is enabled
    if (includeDemo) {
      console.log('[DebtsManager] No user debts found, loading demo data');
      return this.loadDemoData();
    }
    
    return [];
  }

  /**
   * Internal helper for sorting debts by user-defined order
   * @private
   */
  _getSortedDebts(debts) {
    return debts.sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  /**
   * Validate debt data before saving
   * @private
   */
  _validateDebt(debt) {
    if (!debt) {
      throw new Error('Debt object is required');
    }
    
    if (!debt.name || typeof debt.name !== 'string' || debt.name.trim().length === 0) {
      throw new Error('Debt name is required and must be a non-empty string');
    }
    
    const balance = debt.balance ?? debt.amount ?? 0;
    if (typeof balance !== 'number' || balance < 0) {
      throw new Error('Debt balance must be a non-negative number');
    }
    
    const interest = debt.interest ?? debt.interestRate ?? 0;
    if (typeof interest !== 'number' || interest < 0 || interest > 100) {
      throw new Error('Interest rate must be a number between 0 and 100');
    }
    
    const minPayment = debt.minPayment ?? debt.regularPayment ?? 0;
    if (typeof minPayment !== 'number' || minPayment < 0) {
      throw new Error('Minimum payment must be a non-negative number');
    }
  }

  /**
   * Normalize debt fields and apply business rules
   * @private
   */
  _normalizeDebt(debt, { source, note, timestamp }) {
    const balance = debt.balance ?? debt.amount ?? 0;
    const interest = debt.interest ?? debt.interestRate ?? 0;
    const minPayment = debt.minPayment ?? debt.regularPayment ?? this._calculateMinPayment(balance);
    
    return {
      ...debt,
      name: debt.name.trim(),
      balance,
      amount: balance, // Keep both for backward compatibility
      interest,
      interestRate: interest, // Keep both for backward compatibility
      minPayment,
      regularPayment: minPayment, // Keep both for backward compatibility
      // Ensure required metadata
      id: debt.id || this._generateDebtId(),
      order: debt.order ?? this._getNextOrder(),
      // Audit trail
      lastModified: {
        timestamp,
        source,
        note
      }
    };
  }

  /**
   * Calculate minimum payment using canonical rules
   * @private
   */
  _calculateMinPayment(balance) {
    // Canonical rule: 2% of balance, minimum £25
    return Math.max(25, Math.round(balance * 0.02));
  }

  /**
   * Generate unique debt ID
   * @private
   */
  _generateDebtId() {
    return `debt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get next order number for new debts
   * @private
   */
  _getNextOrder() {
    const debts = this.data.debts || [];
    const maxOrder = debts.length > 0 ? Math.max(...debts.map(d => d.order || 0)) : 0;
    return maxOrder + 1;
  }

  /**
   * @deprecated Use getDebts() instead. This method is kept for backward compatibility.
   */
  getDebtsInOrder() {
    console.warn('[DebtsManager] getDebtsInOrder() is deprecated. Use getDebts({ sorted: true }) instead.');
    return this.getDebts({ sorted: true });
  }

  /**
   * CENTRALIZED DEBT WRITER - The Only Official Way to Write Debt Data
   * All components, hooks, and pages should use this method exclusively.
   * Includes validation, normalization, and automatic history tracking.
   * 
   * @param {Object} debt - Debt object to save
   * @param {Object} options - Save options
   * @param {string} options.source - Source of the change (e.g., 'user_edit', 'bulk_import', 'payment')
   * @param {string} options.note - Optional note about the change
   * @returns {Object} Normalized debt object
   */
  saveDebt(debt, { source = 'user_edit', note = null } = {}) {
    const debts = this.data.debts || [];
    const existingIndex = debts.findIndex(d => d.id === debt.id);
    const timestamp = new Date().toISOString();
    
    // Validation
    this._validateDebt(debt);
    
    // Store previous state for milestone detection
    const previousDebts = JSON.parse(JSON.stringify(debts));
    
    // Normalize and validate all fields
    const normalizedDebt = this._normalizeDebt(debt, { source, note, timestamp });
    
    if (existingIndex >= 0) {
      const existing = debts[existingIndex];
      const prevBalance = existing.balance || existing.amount || 0;
      const newBalance = normalizedDebt.balance;
      
      // Enhanced history tracking for balance changes
      if (prevBalance !== newBalance) {
        normalizedDebt.previousBalance = prevBalance;
        
        const history = existing.history || [];
        const changeAmount = newBalance - prevBalance;
        
        // Enhanced history entry with more context
        history.push({
          balance: newBalance,
          previousBalance: prevBalance,
          changeAmount,
          changedAt: timestamp,
          source,
          note,
          // Calculate progress metrics
          isReduction: changeAmount < 0,
          percentChange: prevBalance > 0 ? Math.round((changeAmount / prevBalance) * 100 * 100) / 100 : null
        });
        
        // Limit to last 50 entries to prevent unbounded growth
        normalizedDebt.history = history.slice(-50);
        
        console.log(`[DebtsManager] Balance changed for ${normalizedDebt.name}: £${prevBalance} → £${newBalance} (${changeAmount >= 0 ? '+' : ''}£${changeAmount}) [${source}]`);
      } else {
        // Preserve existing history if balance unchanged
        normalizedDebt.history = existing.history || [];
        normalizedDebt.previousBalance = existing.previousBalance;
        
        // Still log non-balance changes for audit trail
        if (source !== 'user_edit') {
          console.log(`[DebtsManager] Debt updated: ${normalizedDebt.name} [${source}]`);
        }
      }
      
      debts[existingIndex] = { 
        ...existing,
        ...normalizedDebt, 
        updatedAt: timestamp
      };
    } else {
      // New debt creation
      const newDebt = {
        ...normalizedDebt,
        originalAmount: normalizedDebt.balance,
        createdAt: timestamp,
        updatedAt: timestamp,
        // Initialize history with creation entry
        history: [{
          balance: normalizedDebt.balance,
          previousBalance: 0,
          changeAmount: normalizedDebt.balance,
          changedAt: timestamp,
          source,
          note: note || 'Debt created',
          isReduction: false,
          percentChange: null
        }]
      };
      
      debts.push(newDebt);
      console.log(`[DebtsManager] New debt created: ${normalizedDebt.name} (£${normalizedDebt.balance}) [${source}]`);
    }
    
    this.data.debts = debts;
    this.saveData();
    this.recalculateProjections();
    
    // Store milestone detection data
    this.data._lastMilestoneCheck = {
      previousDebts,
      currentDebts: JSON.parse(JSON.stringify(debts)),
      timestamp
    };
    
    return normalizedDebt;
  }

  /**
   * CENTRALIZED BULK DEBT WRITER - For importing or batch operations
   * @param {Array} debts - Array of debt objects to save
   * @param {Object} options - Save options
   * @returns {Array} Array of normalized debt objects
   */
  saveDebts(debts, { source = 'bulk_import', note = null } = {}) {
    if (!Array.isArray(debts)) {
      throw new Error('saveDebts requires an array of debt objects');
    }
    
    console.log(`[DebtsManager] Bulk saving ${debts.length} debts [${source}]`);
    const results = [];
    
    // Process each debt individually to ensure validation and history
    for (const debt of debts) {
      try {
        const result = this.saveDebt(debt, { source, note });
        results.push(result);
      } catch (error) {
        console.error(`[DebtsManager] Failed to save debt ${debt.name || 'unnamed'}:`, error);
        // Continue processing other debts
      }
    }
    
    return results;
  }

  /**
   * Get debt balance history for debugging/analysis
   */
  getDebtHistory(debtId) {
    const debt = this.data.debts.find(d => d.id === debtId);
    return debt?.history || [];
  }

  /**
   * Get all debts with their latest balance changes
   */
  getDebtsWithLatestChanges() {
    return this.data.debts.map(debt => {
      const history = debt.history || [];
      const latestChange = history.length > 1 ? 
        history[history.length - 1].balance - history[history.length - 2].balance : 0;
      
      return {
        ...debt,
        latestChange,
        totalChanges: history.length,
        firstRecorded: history[0]?.changedAt,
        lastChanged: history[history.length - 1]?.changedAt
      };
    });
  }

  /**
   * Debug helper: Export debt history as CSV-like data
   */
  exportDebtHistory(debtId = null) {
    if (debtId) {
      const debt = this.data.debts.find(d => d.id === debtId);
      if (!debt) return null;
      
      return {
        debtName: debt.name,
        history: debt.history || []
      };
    }
    
    // Export all debts
    return this.data.debts.map(debt => ({
      debtName: debt.name,
      id: debt.id,
      currentBalance: debt.balance || debt.amount || 0,
      originalAmount: debt.originalAmount || 0,
      totalReduction: (debt.originalAmount || 0) - (debt.balance || debt.amount || 0),
      history: debt.history || []
    }));
  }

  /**
   * Debug payment scenarios: Compare different payment amounts
   * Perfect for debugging why £1941 vs £2191 doesn't change timeline
   */
  analyzePayments(payment1 = 1941, payment2 = 2191, maxMonths = 60) {
    console.log(`🔍 ANALYZING PAYMENTS: £${payment1} vs £${payment2}`);
    console.log('='.repeat(50));
    
    const debts = this.getDebts();
    if (debts.length === 0) {
      console.log('❌ No debts found for analysis');
      return null;
    }

    // Calculate total minimums
    const totalMinimums = debts.reduce((sum, debt) => {
      const minPayment = debt.minPayment || debt.min || debt.regularPayment || Math.max(25, Math.floor((debt.balance || debt.amount || 0) * 0.02));
      return sum + minPayment;
    }, 0);

    console.log(`💰 Current Debts:`, debts.map(d => `${d.name}: £${d.balance || d.amount || 0}`));
    console.log(`📊 Total Debt: £${debts.reduce((sum, d) => sum + (d.balance || d.amount || 0), 0)}`);
    console.log(`💳 Total Minimums: £${totalMinimums}/month`);
    console.log(`🎯 Extra Payment 1: £${payment1 - totalMinimums}`);
    console.log(`🎯 Extra Payment 2: £${payment2 - totalMinimums}`);
    console.log('');

    // Simulate both scenarios
    const scenario1 = this.simulatePayoffScenario(debts, payment1, maxMonths);
    const scenario2 = this.simulatePayoffScenario(debts, payment2, maxMonths);

    console.log(`📅 RESULTS:`);
    console.log(`   Payment £${payment1}: ${scenario1.payoffMonth > 0 ? scenario1.payoffMonth + ' months' : 'Not paid off in ' + maxMonths + ' months'}`);
    console.log(`   Payment £${payment2}: ${scenario2.payoffMonth > 0 ? scenario2.payoffMonth + ' months' : 'Not paid off in ' + maxMonths + ' months'}`);
    
    if (scenario1.payoffMonth > 0 && scenario2.payoffMonth > 0) {
      const timeSaved = scenario1.payoffMonth - scenario2.payoffMonth;
      console.log(`   ⏰ Time difference: ${timeSaved} months ${timeSaved > 0 ? 'faster with higher payment' : 'no difference'}`);
      console.log(`   💰 Interest saved: £${Math.round(scenario1.totalInterest - scenario2.totalInterest)}`);
    }

    console.log('');
    console.log(`📈 MONTH-BY-MONTH COMPARISON (first 12 months):`);
    console.log('Month | Payment1 | Payment2 | Difference');
    console.log('------|----------|----------|----------');
    
    for (let i = 0; i < Math.min(12, maxMonths); i++) {
      const bal1 = scenario1.monthlyBalances[i] || 0;
      const bal2 = scenario2.monthlyBalances[i] || 0;
      const diff = bal1 - bal2;
      console.log(`  ${String(i + 1).padStart(2)}  |  £${String(bal1).padStart(6)} |  £${String(bal2).padStart(6)} |   £${diff >= 0 ? '+' : ''}${diff}`);
    }

    return {
      scenario1: { payment: payment1, months: scenario1.payoffMonth, interest: scenario1.totalInterest },
      scenario2: { payment: payment2, months: scenario2.payoffMonth, interest: scenario2.totalInterest },
      timeDifference: scenario1.payoffMonth > 0 && scenario2.payoffMonth > 0 ? scenario1.payoffMonth - scenario2.payoffMonth : null,
      interestSaved: scenario1.totalInterest - scenario2.totalInterest,
      monthlyBalances: { scenario1: scenario1.monthlyBalances, scenario2: scenario2.monthlyBalances }
    };
  }

  /**
   * Simulate debt payoff with snowball method
   */
  simulatePayoffScenario(debts, totalPayment, maxMonths) {
    // Sort debts by user-defined order first, then by balance as fallback
    const workingDebts = JSON.parse(JSON.stringify(debts))
      .map(debt => ({
        name: debt.name,
        balance: debt.balance || debt.amount || 0,
        rate: debt.interest || debt.rate || 20, // Default 20% if no rate specified
        minPayment: debt.minPayment || debt.min || debt.regularPayment || Math.max(25, Math.floor((debt.balance || debt.amount || 0) * 0.02)),
        order: debt.order || 999
      }))
      .sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.balance - b.balance; // Fallback to balance if orders are equal  
      });

    const monthlyBalances = [];
    let totalInterest = 0;
    let payoffMonth = -1;

    for (let month = 0; month < maxMonths; month++) {
      const totalBalance = workingDebts.reduce((sum, debt) => sum + debt.balance, 0);
      monthlyBalances.push(Math.round(totalBalance));

      if (totalBalance <= 1) {
        payoffMonth = month;
        break;
      }

      let availablePayment = totalPayment;

      // Apply minimum payments first
      for (const debt of workingDebts) {
        if (debt.balance <= 0) continue;
        
        const monthlyInterest = debt.balance * (debt.rate / 12 / 100);
        totalInterest += monthlyInterest;
        const principal = Math.max(0, debt.minPayment - monthlyInterest);
        
        debt.balance = Math.max(0, debt.balance - principal);
        availablePayment -= debt.minPayment;
      }

      // Apply extra payment to smallest debt
      if (availablePayment > 0) {
        for (const debt of workingDebts) {
          if (debt.balance > 0) {
            const extraPayment = Math.min(availablePayment, debt.balance);
            debt.balance -= extraPayment;
            break;
          }
        }
      }
    }

    return {
      payoffMonth,
      totalInterest: Math.round(totalInterest),
      monthlyBalances
    };
  }

  /**
   * Delete a debt
   */
  deleteDebt(debtId) {
    this.data.debts = this.data.debts.filter(debt => debt.id !== debtId);
    this.data.paymentHistory = this.data.paymentHistory.filter(
      payment => payment.debtId !== debtId
    );
    this.saveData();
    this.recalculateProjections();
  }

  /**
   * Reorder debts by updating their order field
   * @param {Array<{id: string, order: number}>} orderUpdates
   */
  reorderDebts(orderUpdates) {
    const debts = this.data.debts || [];
    
    orderUpdates.forEach(({ id, order }) => {
      const debtIndex = debts.findIndex(d => d.id === id);
      if (debtIndex >= 0) {
        debts[debtIndex].order = order;
        debts[debtIndex].updatedAt = new Date().toISOString();
      }
    });
    
    this.data.debts = debts;
    this.saveData();
    this.recalculateProjections();
  }


  /**
   * Set all debts (bulk operation)
   */
  setDebts(debts) {
    this.data.debts = debts.map(debt => ({
      ...debt,
      id: debt.id || Date.now().toString() + Math.random().toString(36).substr(2, 9),
      originalAmount: debt.originalAmount || debt.amount,
      createdAt: debt.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }));
    this.saveData();
    this.recalculateProjections();
  }

  // PAYMENT HISTORY

  /**
   * Record a payment
   */
  recordPayment(payment) {
    const payments = this.data.paymentHistory || [];
    const existingIndex = payments.findIndex(
      p => p.month === payment.month && p.debtId === payment.debtId
    );

    const paymentRecord = {
      ...payment,
      id: payment.id || Date.now().toString() + Math.random().toString(36).substr(2, 9),
      recordedAt: new Date().toISOString()
    };

    let paymentDifference = paymentRecord.amount;
    if (existingIndex >= 0) {
      paymentDifference = paymentRecord.amount - payments[existingIndex].amount;
      payments[existingIndex] = paymentRecord;
    } else {
      payments.push(paymentRecord);
    }

    // Update debt balance using centralized writer with enhanced history
    const debt = this.data.debts.find(d => d.id === payment.debtId);
    if (debt && paymentDifference !== 0) {
      const prevBalance = debt.balance || debt.amount || 0;
      const newBalance = Math.max(0, prevBalance - paymentDifference);
      
      // Use centralized writer for consistent validation and history
      this.saveDebt({
        ...debt,
        balance: newBalance
      }, {
        source: 'payment_recorded',
        note: `Payment of £${paymentRecord.amount} recorded for ${payment.month}`
      });
    }

    this.data.paymentHistory = payments;
    this.saveData();
    this.recalculateProjections();
    return paymentRecord;
  }

  /**
   * Get payment history
   */
  getPaymentHistory(month = null) {
    if (month) {
      return this.data.paymentHistory.filter(payment => payment.month === month);
    }
    return this.data.paymentHistory || [];
  }

  /**
   * Get specific payment
   */
  getPayment(debtId, month) {
    return this.data.paymentHistory.find(
      payment => payment.debtId === debtId && payment.month === month
    );
  }

  // PROJECTIONS & CALCULATIONS

  /**
   * Calculate debt projections using centralized accessor
   */
  calculateProjections() {
    const debts = this.getDebts(); // Use centralized accessor (already sorted)
    const extraPayment = this.data.settings.extraPayment || 0;
    
    if (debts.length === 0) {
      return { months: [], totalMonths: 0, totalInterest: 0 };
    }

    try {
      const engine = new DebtEngine(debts);
      const totalMinPayments = debts.reduce((sum, debt) => 
        sum + debt.minPayment, 0); // minPayment is now normalized
      const projections = engine.generateTimeline(totalMinPayments + extraPayment);

      const result = {
        months: projections,
        totalMonths: projections.length,
        totalInterest: projections.reduce((sum, month) => sum + (month.totalInterest || 0), 0),
        calculatedAt: new Date().toISOString()
      };

      this.data.projections = result;
      this.saveData();
      return result;
    } catch (error) {
      console.error('[DebtsManager] Error calculating projections:', error);
      return { months: [], totalMonths: 0, totalInterest: 0 };
    }
  }

  /**
   * Get projections (calculate if needed)
   */
  getProjections() {
    if (!this.data.projections) {
      return this.calculateProjections();
    }
    return this.data.projections;
  }

  /**
   * Force recalculation of projections
   */
  recalculateProjections() {
    return this.calculateProjections();
  }

  // SETTINGS

  /**
   * Update settings
   */
  updateSettings(newSettings) {
    this.data.settings = { ...this.data.settings, ...newSettings };
    this.saveData();
    
    if (newSettings.extraPayment !== undefined) {
      this.recalculateProjections();
    }
  }

  /**
   * Get settings
   */
  getSettings() {
    return this.data.settings || DEFAULT_DATA.settings;
  }

  // UTILITIES

  /**
   * Get month key for calculations
   */
  getMonthKey(monthOffset = 0) {
    const date = new Date();
    date.setMonth(date.getMonth() + monthOffset);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  /**
   * Clear all data
   */
  clearAllData() {
    this.data = { ...DEFAULT_DATA };
    this.saveData();
  }

  /**
   * Export data
   */
  exportData() {
    return {
      ...this.data,
      exportedAt: new Date().toISOString(),
      version: '2.0.0'
    };
  }

  /**
   * Import data
   */
  importData(importedData) {
    this.data = {
      ...DEFAULT_DATA,
      ...importedData,
      importedAt: new Date().toISOString()
    };
    this.saveData();
  }

  /**
   * Load demo data with realistic UK credit card debts
   */
  loadDemoData() {
    // Fixed demo data for consistency across all pages
    const getStandardDemoDebts = () => {
      return [
        {
          id: 'demo_paypal_1',
          name: 'PayPal Credit',
          balance: 1400,
          amount: 1400,
          limit: 2000,
          interest: 20.9,
          rate: 20.9,
          regularPayment: 35,
          minPayment: 35,
          order: 1,
          type: 'Credit Card',
          isDemo: true,
          createdAt: new Date('2024-01-01').toISOString(),
          updatedAt: new Date().toISOString(),
          originalAmount: 2020
        },
        {
          id: 'demo_barclaycard_2',
          name: 'Barclaycard',
          balance: 2461,
          amount: 2461,
          limit: 4000,
          interest: 19.9,
          rate: 19.9,
          regularPayment: 75,
          minPayment: 75,
          order: 2,
          type: 'Credit Card',
          isDemo: true,
          createdAt: new Date('2024-01-01').toISOString(),
          updatedAt: new Date().toISOString(),
          originalAmount: 2863
        },
        {
          id: 'demo_halifax_3',
          name: 'Halifax Credit Card',
          balance: 5931,
          amount: 5931,
          limit: 8000,
          interest: 18.9,
          rate: 18.9,
          regularPayment: 155,
          minPayment: 155,
          order: 3,
          type: 'Credit Card',
          isDemo: true,
          createdAt: new Date('2024-01-01').toISOString(),
          updatedAt: new Date().toISOString(),
          originalAmount: 6248
        },
        {
          id: 'demo_mbna_4',
          name: 'MBNA Card',
          balance: 8720,
          amount: 8720,
          limit: 12000,
          interest: 21.9,
          rate: 21.9,
          regularPayment: 220,
          minPayment: 220,
          order: 4,
          type: 'Credit Card',
          isDemo: true,
          createdAt: new Date('2024-01-01').toISOString(),
          updatedAt: new Date().toISOString(),
          originalAmount: 8720
        }
      ];
    };

    const demoDebts = getStandardDemoDebts();
    this.setDebts(demoDebts);
    console.log('Demo data loaded:', demoDebts.map(d => `${d.name}: £${d.balance}`));
    return demoDebts;
  }

  /**
   * Get computed metrics using centralized debt accessor
   */
  getMetrics() {
    const debts = this.getDebts(); // Use centralized accessor
    const totalDebt = debts.reduce((sum, debt) => sum + debt.balance, 0); // balance is now normalized
    const totalMinPayments = debts.reduce((sum, debt) => sum + debt.minPayment, 0); // minPayment is now normalized
    const extraPayment = this.data.settings.extraPayment || 0;
    
    return {
      totalDebt,
      totalMinPayments,
      extraPayment,
      totalPayment: totalMinPayments + extraPayment,
      isDebtFree: debts.length === 0 || totalDebt === 0,
      hasProjections: this.data.projections && this.data.projections.months.length > 0
    };
  }
}

// Create singleton instance
export const debtsManager = new DebtsManager();
export default debtsManager;